/**
 * Auth Provider
 * Manages authentication state and profile data
 */

import React, { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../firebase';
import type { BackendUser, ProfileData } from '@/types/api';
import { getAccessToken, isTokenExpired, saveTokens, clearTokens, getRoleFromToken } from '@/utils/storage';
import { profilesApi } from '@/api/services/profilesApi';
import { refreshAccessToken } from '@/api/services/authApi';
import { authService } from '../services/authService';
import { AuthContext, type AuthContextType } from './AuthContextDefinition';
import { logger } from '@/utils/logger';
import posthog from '@/analytics';
import { captureError, pushBreadcrumb } from '@/debug/debugStore';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  const tokenRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const setupRefreshTimer = () => {
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
    }

    tokenRefreshTimer.current = setInterval(async () => {
      try {
        if (isTokenExpired(5)) {
          const { jwtToken, refreshToken } = await refreshAccessToken();
          saveTokens(jwtToken, refreshToken);
        }
      } catch (error) {
        logger.error('AuthProvider', 'Token refresh failed', error);
        captureError('AuthProvider:tokenRefresh', error);
        pushBreadcrumb({ category: 'system', message: 'Token refresh failed (timer)' });
        clearTokens();
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }, 5 * 60 * 1000);
  };

  const clearRefreshTimer = () => {
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }
  };

  useEffect(() => {
    const storedProfile = localStorage.getItem('client_userProfile');
    if (storedProfile) {
      try {
        setProfile(JSON.parse(storedProfile));
      } catch (error) {
        logger.error('AuthProvider', 'Failed to parse stored profile', error);
        localStorage.removeItem('client_userProfile');
      }
    }
  }, []);

  // 2. Subscribe to auth state changes and fetch profile
  useEffect(() => {
    const fetchUserProfile = async (role: 'tutor' | 'parent'): Promise<boolean> => {
      try {
        const profileData = await profilesApi.getProfile(role);
        setProfile(profileData);
        // Persist to localStorage
        localStorage.setItem('client_userProfile', JSON.stringify(profileData));
        return true;
      } catch (error: unknown) {
        const apiCode = (error as { code?: string })?.code;
        const httpStatus = (error as { details?: { httpStatus?: number } })?.details?.httpStatus
          ?? (error as { response?: { status?: number } })?.response?.status;
const isNotFound = apiCode === 'HTTP_404' || apiCode === 'HTTP_400' || httpStatus === 404 || httpStatus === 400;
        if (isNotFound) {
          // Profile doesn't exist yet (user skipped registration)
          // Clear all auth state to prevent infinite broken loop
          logger.warn('AuthProvider', 'Profile not found, clearing auth state');
          clearTokens();
          setBackendUser(null);
          setProfile(null);
          localStorage.removeItem('client_userProfile');
          localStorage.removeItem('client_userRole');
          clearRefreshTimer();
          await auth.signOut().catch((err) => logger.error('AuthProvider', 'Sign out error', err));
          setFirebaseUser(null);
        } else {
          logger.error('AuthProvider', 'Error fetching user profile', error);
          captureError('AuthProvider:profileFetch', error);
        }
        return false;
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      const isVerified = user?.emailVerified ?? false;

      if (user && isVerified) {
        // Guard: only proceed if a client-side token exists.
        // This prevents the client app from reacting to admin logins that share the same Firebase session.
        const existingToken = getAccessToken();
        if (!existingToken) {
          setLoading(false);
          return;
        }

        // Set firebaseUser immediately so NavBar re-renders without waiting for async profile fetch
        setFirebaseUser(user);

        let token = existingToken;

        // 1. Check if token is expired and attempt refresh
        if (token && isTokenExpired(1)) {
          try {
            logger.log('AuthProvider', 'Token expired on initialization, attempting refresh...');
            const { jwtToken, refreshToken: newRefreshToken } = await refreshAccessToken();
            saveTokens(jwtToken, newRefreshToken);
            token = jwtToken;
            logger.log('AuthProvider', 'Token refresh successful');
            pushBreadcrumb({ category: 'system', message: 'Token refresh success (init)' });
          } catch (error) {
            logger.error('AuthProvider', 'Token refresh failed on initialization', error);
            captureError('AuthProvider:initRefresh', error);
            pushBreadcrumb({ category: 'system', message: 'Token refresh failed (init)' });
            // Only if refresh fails, we clear everything
            clearTokens();
            setBackendUser(null);
            setProfile(null);
            localStorage.removeItem('client_userProfile');
            localStorage.removeItem('client_userRole');
            localStorage.removeItem('client_backendUser');
            clearRefreshTimer();

            await auth.signOut().catch((err) => logger.error('AuthProvider', 'Sign out error', err));
            setFirebaseUser(null);
            setLoading(false);
            return;
          }
        }

        // 2. Fetch/Restore profile if we have a token
        if (token) {
          const role = getRoleFromToken() || (localStorage.getItem('client_userRole') as 'tutor' | 'parent' | null);

          if (role) {
            const profileFetched = await fetchUserProfile(role);
            if (!profileFetched) {
              setLoading(false);
              return;
            }
          }
        }

        // Restore full backend user from localStorage if available, otherwise use minimal Firebase data
        const storedBackendUser = localStorage.getItem('client_backendUser');
        if (storedBackendUser) {
          try {
            const parsed = JSON.parse(storedBackendUser);
            setBackendUser(parsed);
          } catch {
            setBackendUser({
              firebaseUid: user.uid,
              email: user.email,
              emailVerified: user.emailVerified,
            } as BackendUser);
          }
        } else {
          setBackendUser({
            firebaseUid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
          } as BackendUser);
        }

        setupRefreshTimer();
      } else {
        // Only clear everything if we are transition-free or definitely logged out
        setFirebaseUser(null);
        setBackendUser(null);
        setProfile(null);
        localStorage.removeItem('client_userProfile');
        localStorage.removeItem('client_userRole');
        localStorage.removeItem('client_backendUser');
        clearRefreshTimer();
      }

      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      clearRefreshTimer();
    };
  }, []);

  useEffect(() => {
    const handleLogout = () => {
      setFirebaseUser(null);
      setBackendUser(null);
      setProfile(null);
      localStorage.removeItem('client_userProfile');
      localStorage.removeItem('client_userRole');
      localStorage.removeItem('client_backendUser');
      clearRefreshTimer();

      auth.signOut().catch((error) => {
        logger.error('AuthProvider', 'Sign out error', error);
      });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  useEffect(() => {
    if (backendUser?.userId) {
      posthog.identify(backendUser.userId, {
        email: backendUser.email,
        role: backendUser.currentRole,
      });
    } else {
      posthog.reset();
    }
  }, [backendUser]);

  const switchRole = async (newRole: 'tutor' | 'parent') => {
    const data = await authService.switchRole(newRole);
    // Update backend user state and persist
    setBackendUser(data.user as BackendUser);
    localStorage.setItem('client_backendUser', JSON.stringify(data.user));
    // Update profile to the newly active role's profile
    const newProfile = newRole === 'tutor' ? data.tutorProfile : data.parentProfile;
    if (newProfile) {
      setProfile(newProfile);
      localStorage.setItem('client_userProfile', JSON.stringify(newProfile));
    }
    localStorage.setItem('client_userRole', newRole);
    pushBreadcrumb({ category: 'system', message: `Role switched to ${newRole}` });
  };

  const value: AuthContextType = {
    firebaseUser,
    backendUser,
    loading,
    profile,
    updateFirebaseUser: setFirebaseUser,
    updateBackendUser: (user) => {
      setBackendUser(user);
      if (user) {
        localStorage.setItem('client_backendUser', JSON.stringify(user));
      } else {
        localStorage.removeItem('client_backendUser');
      }
    },
    updateProfile: (newProfile) => {
      setProfile(newProfile);
      if (newProfile) {
        localStorage.setItem('client_userProfile', JSON.stringify(newProfile));
      } else {
        localStorage.removeItem('client_userProfile');
      }
    },
    switchRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
